import React, {useEffect} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {toast} from 'react-toastify';
import {useTranslation} from 'react-i18next';
import {QUERY_KEYS} from '../../../utils/queryKeys';
import {
    createPresentationPrototype,
    fetchPresentationPrototype,
    fetchPresentationPrototypePromptHistory,
    generatePresentationPrototype,
    modifyPresentationPrototype,
    organizePrototypeBySlides,
    revertPresentationPrototypeToPrompt,
    updatePresentationPrototype,
} from '../../../service/presentationPrototype.service';
import {PresentationPrototype, PresentationPrototypeItemDTO, PresentationPrototypeType} from '../../../incase-domain/presentationPrototype';
import {useGenerateAIPresentation} from '../../components/common/aiGeneratePresentation/queries';
import {SlideSize} from './types';

export interface OrganizePrototypeOptions {
    slideSize?: SlideSize;
    pages?: string[];
    content?: string;
}

interface UsePresentationPrototypeOptions {
    onInsertSuccess?: (presentationId: string) => void;
}

export interface UsePresentationPrototypeResult {
    prototype: PresentationPrototype | undefined;
    setPrototype: React.Dispatch<React.SetStateAction<PresentationPrototype | undefined>>;
    promptHistory: PresentationPrototypeItemDTO[];
    error: string | undefined;
    isLoading: boolean;
    generate: (params: {prompt: string; type?: PresentationPrototypeType; id?: string}, onSuccess?: () => void) => void;
    modify: (prompt: string, onSuccess?: () => void) => void;
    update: (explicit?: PresentationPrototype, onSuccess?: () => void) => void;
    organize: (opts: OrganizePrototypeOptions) => void;
    revert: (id: string) => void;
    insert: (content: string) => void;
    createNew: (type?: PresentationPrototypeType) => void;
}

export function usePresentationPrototype(options?: UsePresentationPrototypeOptions): UsePresentationPrototypeResult {
    const {t} = useTranslation();
    const queryClient = useQueryClient();

    const [prototype, setPrototype] = React.useState<PresentationPrototype | undefined>();
    const [error, setError] = React.useState<string | undefined>();

    const {data: presProto} = useQuery(QUERY_KEYS.presentationPrototype.all, () => fetchPresentationPrototype(), {
        refetchOnWindowFocus: false,
    });

    const {data: prompts} = useQuery(
        QUERY_KEYS.presentationPrototype.promptHistory(prototype?._id || ''),
        () => fetchPresentationPrototypePromptHistory(prototype?._id || ''),
        {
            enabled: !!prototype?._id,
            refetchOnWindowFocus: false,
        }
    );

    useEffect(() => {
        if (presProto?._id) {
            setPrototype(presProto);
        }
    }, [presProto]);

    const invalidatePromptHistory = (): void => {
        queryClient.invalidateQueries({queryKey: QUERY_KEYS.presentationPrototype.sub('promptHistory')});
    };

    const onErrorFor =
        (translationKey: `generator.errors.${'generate' | 'modify' | 'update' | 'organize' | 'revert' | 'create'}`) =>
        (mutationError: unknown): void => {
            setError(t(translationKey, {message: String(mutationError)}));
        };

    const {mutate: generateMutation, isLoading: isGenerating} = useMutation(
        (params: {prompt: string; type?: PresentationPrototypeType; id?: string}) => generatePresentationPrototype(params),
        {
            onSuccess: (data) => {
                setPrototype(data);
                setError(undefined);
                invalidatePromptHistory();
                toast(t('generator.generationSuccess'), {type: 'success'});
            },
            onError: onErrorFor('generator.errors.generate'),
        }
    );

    const {mutate: modifyMutation, isLoading: isModifying} = useMutation(
        (prompt: string) => {
            if (!prototype?._id) throw new Error('No prototype to modify');
            return modifyPresentationPrototype({id: prototype._id, prompt});
        },
        {
            onSuccess: (data) => {
                setPrototype(data);
                setError(undefined);
                invalidatePromptHistory();
                toast(t('generator.modificationSuccess'), {type: 'success'});
            },
            onError: onErrorFor('generator.errors.modify'),
        }
    );

    const {mutate: updateMutation, isLoading: isUpdating} = useMutation(
        (explicit: PresentationPrototype | void) => {
            const p = explicit ?? prototype;
            if (!p) throw new Error('No prototype to update');
            return updatePresentationPrototype(p);
        },
        {
            onSuccess: (data) => {
                setPrototype(data);
                setError(undefined);
                toast(t('generator.updateSuccess'), {type: 'success'});
            },
            onError: onErrorFor('generator.errors.update'),
        }
    );

    const {mutate: organizeMutation, isLoading: isOrganizing} = useMutation(
        (opts: OrganizePrototypeOptions) => {
            if (!prototype?._id) throw new Error('No prototype to organize');
            const isAlreadyOrganized = /^##\s+Slide\s+\d+/m.test(prototype.content ?? '');
            const usePdfPages = !!opts.pages && opts.pages.length > 1 && !isAlreadyOrganized;
            return organizePrototypeBySlides(
                prototype._id,
                opts.slideSize,
                usePdfPages ? opts.pages : undefined,
                usePdfPages ? undefined : opts.content ?? prototype.content
            );
        },
        {
            onSuccess: (data) => {
                setPrototype(data);
                setError(undefined);
                invalidatePromptHistory();
                toast(t('generator.organizeSuccess'), {type: 'success'});
            },
            onError: onErrorFor('generator.errors.organize'),
        }
    );

    const {mutate: revertMutation, isLoading: isReverting} = useMutation((id: string) => revertPresentationPrototypeToPrompt(id), {
        onSuccess: (data) => {
            setPrototype(data);
            setError(undefined);
            invalidatePromptHistory();
            toast(t('generator.revertSuccess'), {type: 'success'});
        },
        onError: onErrorFor('generator.errors.revert'),
    });

    const {mutate: insertMutation, isLoading: isInsertingIntoNCC} = useGenerateAIPresentation({
        onSuccess: (result) => {
            toast(t('generator.insertSuccessHeader'), {type: 'success'});
            options?.onInsertSuccess?.(result._id);
        },
    });

    const {mutate: createNewMutation, isLoading: isCreatingNew} = useMutation(
        (newType: PresentationPrototypeType | undefined) => createPresentationPrototype(newType),
        {
            onSuccess: (data) => {
                setPrototype(data);
                setError(undefined);
                invalidatePromptHistory();
            },
            onError: onErrorFor('generator.errors.create'),
        }
    );

    const generate = (params: {prompt: string; type?: PresentationPrototypeType; id?: string}, onSuccess?: () => void): void =>
        generateMutation(params, onSuccess ? {onSuccess} : undefined);
    const modify = (prompt: string, onSuccess?: () => void): void => modifyMutation(prompt, onSuccess ? {onSuccess} : undefined);
    const update = (explicit?: PresentationPrototype, onSuccess?: () => void): void =>
        updateMutation(explicit, onSuccess ? {onSuccess} : undefined);
    const organize = (opts: OrganizePrototypeOptions): void => organizeMutation(opts);
    const revert = (id: string): void => revertMutation(id);
    const insert = (content: string): void => insertMutation({content: [content], images: []});

    const createNew = (newType?: PresentationPrototypeType): void => createNewMutation(newType);

    const isLoading = isGenerating || isModifying || isUpdating || isReverting || isOrganizing || isInsertingIntoNCC || isCreatingNew;

    return {
        prototype,
        setPrototype,
        promptHistory: prompts ?? [],
        error,
        isLoading,
        generate,
        modify,
        update,
        organize,
        revert,
        insert,
        createNew,
    };
}
